import { useContext, useEffect, useRef } from "react";
import { ViewerContext } from "dash-ui";
import { ProductSubscriptionStatusesEnum } from "dash-common";

import { identify, setTraits } from "shared/analytics";
import { readFirstAttribution, readLastAttribution } from "shared/attribution";

// Drives the "Internal team" exclusion in PostHog's test-account filter.
const INTERNAL_EMAIL_DOMAINS = ["dashtodash.com", "timofi.app"];

/**
 * Flattens the UTM cookie into person properties. First touch answers "which
 * channel acquired this account" and is written set-once so a later campaign
 * click can never overwrite it; last touch answers "what brought them back".
 */
const attributionTraits = (
  attribution: ReturnType<typeof readFirstAttribution>,
  prefix: "initial" | "last"
): Record<string, string> => {
  if (!attribution) return {};
  return Object.fromEntries(
    Object.entries(attribution).map(([key, value]) => [
      `${prefix}_${key}`,
      value,
    ])
  );
};

/**
 * Resolves the current session to a PostHog person and keeps plan/subscription
 * traits current. Never sends email, phone, name, Stripe ids, sso id, or an
 * avatar url — only ids/enums/booleans/dates already surfaced on ViewerContext.
 * `is_internal` is derived from the email domain in the browser; the email
 * itself never leaves.
 */
export const useAccountAnalyticsIdentity = () => {
  const { account, broker, subscription, userHasTrialed } =
    useContext(ViewerContext);

  const accountId = account?._id ?? null;
  const isSeeker = account?.isSeeker ?? broker?.isSeeker ?? false;
  const plan = subscription?.subscriptionProduct?.name ?? null;
  const planRecurringType =
    subscription?.subscriptionProduct?.recurringType ?? null;
  const subscriptionStatus = subscription?.status ?? null;
  const isTrialing =
    subscription?.status === ProductSubscriptionStatusesEnum.TRIALING;
  const showAllBillingPlans = !!account?.showAllBillingPlans;
  const hasEverTrialed = !!userHasTrialed;
  const accountType = isSeeker ? "seeker" : "broker";
  const emailDomain = account?.email?.split("@").pop()?.toLowerCase() ?? "";
  const isInternal = INTERNAL_EMAIL_DOMAINS.includes(emailDomain);

  const identifiedAccountIdRef = useRef<string | null>(null);

  // Fires once per account — resolves the anonymous session to this person.
  useEffect(() => {
    if (!accountId || identifiedAccountIdRef.current === accountId) return;
    identifiedAccountIdRef.current = accountId;

    identify(accountId, {
      plan,
      plan_recurring_type: planRecurringType,
      subscription_status: subscriptionStatus,
      is_trialing: isTrialing,
      has_ever_trialed: hasEverTrialed,
      account_type: accountType,
      account_created_at: account?.createdAt
        ? new Date(account.createdAt).toISOString().split("T")[0]
        : null,
      sso_provider: account?.provider ?? null,
      signed_up_via_sso: !!account?.ssoId,
      dial_code: account?.phone?.dialCode ?? null,
      show_all_billing_plans: showAllBillingPlans,
      is_internal: isInternal,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Keeps traits current when plan/subscription state changes mid-session
  // (e.g. an upgrade), via setPersonProperties rather than re-running identify.
  useEffect(() => {
    if (!accountId) return;

    setTraits(
      {
        plan,
        plan_recurring_type: planRecurringType,
        subscription_status: subscriptionStatus,
        is_trialing: isTrialing,
        has_ever_trialed: hasEverTrialed,
        account_type: accountType,
        show_all_billing_plans: showAllBillingPlans,
        is_internal: isInternal,
        ...attributionTraits(readLastAttribution(), "last"),
      },
      attributionTraits(readFirstAttribution(), "initial")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    plan,
    planRecurringType,
    subscriptionStatus,
    isTrialing,
    hasEverTrialed,
    accountType,
    showAllBillingPlans,
    isInternal,
  ]);
};
